
import React from 'react';

export const PropertyValuationMethods: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Professional Property Valuation Methods
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding how professionals value apartment buildings helps you set realistic expectations and 
            identify opportunities to maximize your property's worth before selling.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Appraisal Process</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="text-blue-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Property Inspection</h4>
                    <p className="text-gray-600 text-sm">
                      Comprehensive examination of the building's physical condition, layout, amenities, 
                      and mechanical systems. Photos and detailed notes are taken.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="text-green-600 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Market Analysis</h4>
                    <p className="text-gray-600 text-sm">
                      Review of comparable sales, current market conditions, rent levels, 
                      and economic factors affecting property values in the area.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="text-yellow-600 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Income Verification</h4>
                    <p className="text-gray-600 text-sm">
                      Analysis of rent rolls, lease agreements, operating expenses, 
                      and financial performance to determine income potential.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="text-purple-600 font-bold text-sm">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Value Conclusion</h4>
                    <p className="text-gray-600 text-sm">
                      Integration of all three approaches (income, sales comparison, cost) 
                      to arrive at a final opinion of market value.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-teal-50 p-6 rounded-xl mt-6">
                <h4 className="text-lg font-semibold text-teal-800 mb-3">Typical Appraisal Timeline</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Property Inspection:</span>
                    <span className="font-medium">1-2 days</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Research & Analysis:</span>
                    <span className="font-medium">5-10 days</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Report Preparation:</span>
                    <span className="font-medium">3-5 days</span>
                  </div>
                  <div className="flex justify-between font-bold text-teal-800 border-t border-teal-200 pt-2">
                    <span>Total Timeline:</span>
                    <span>10-20 days</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/8dd393f9d0fae0c69a0170deab33b055.jpg"
              alt="Professional property appraisal and inspection process"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Comparative Market Analysis (CMA)</h3>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h4 className="text-xl font-semibold text-gray-900 mb-6">What CMA Includes</h4>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-blue-800 mb-2">Recently Sold Properties</h5>
                  <p className="text-gray-700 text-sm">
                    Comparable apartment buildings sold within the last 6-12 months, adjusted for differences 
                    in size, condition, location, and amenities.
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-green-800 mb-2">Active Listings</h5>
                  <p className="text-gray-700 text-sm">
                    Properties currently on the market, providing insight into competition and 
                    current pricing trends in your area.
                  </p>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-yellow-800 mb-2">Pending Sales</h5>
                  <p className="text-gray-700 text-sm">
                    Properties under contract, indicating current market activity and 
                    buyer interest at various price points.
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-purple-800 mb-2">Market Trends</h5>
                  <p className="text-gray-700 text-sm">
                    Analysis of price trends, days on market, and absorption rates to 
                    predict future market conditions.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xl font-semibold text-gray-900 mb-6">Key Adjustment Factors</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Property Size</span>
                  <span className="font-medium text-gray-900">$50-150/sq ft</span>
                </div>

                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Unit Count</span>
                  <span className="font-medium text-gray-900">$15K-40K/unit</span>
                </div>

                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Age & Condition</span>
                  <span className="font-medium text-gray-900">5-25% adjustment</span>
                </div>

                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Location Quality</span>
                  <span className="font-medium text-gray-900">10-30% adjustment</span>
                </div>

                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Income Level</span>
                  <span className="font-medium text-gray-900">Cap rate differential</span>
                </div>

                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Financing Terms</span>
                  <span className="font-medium text-gray-900">2-8% adjustment</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=comparative%20market%20analysis%20spreadsheet%20with%20apartment%20building%20sales%20data%2C%20real%20estate%20market%20trends%20and%20pricing%20analysis%2C%20professional%20CMA%20report%20with%20property%20comparisons%20and%20adjustments&width=600&height=400&seq=cma-market-analysis&orientation=landscape"
              alt="Comparative market analysis and property pricing trends"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Market Value Indicators</h3>
              
              <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-800 mb-3">Strong Market Indicators</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                      <span className="text-gray-700">Declining cap rates (rising prices)</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                      <span className="text-gray-700">Multiple offers on listings</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                      <span className="text-gray-700">Properties selling above asking</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                      <span className="text-gray-700">Low inventory levels</span>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-red-800 mb-3">Weak Market Indicators</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <i className="ri-arrow-down-line text-red-600 mr-2"></i>
                      <span className="text-gray-700">Rising cap rates (falling prices)</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-down-line text-red-600 mr-2"></i>
                      <span className="text-gray-700">Extended days on market</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-down-line text-red-600 mr-2"></i>
                      <span className="text-gray-700">Price reductions common</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-down-line text-red-600 mr-2"></i>
                      <span className="text-gray-700">High inventory buildup</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    <strong>Pro Tip:</strong> Market timing can impact your sale price by 10-20%. 
                    Understanding these indicators helps you decide when to list your property.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">Professional Valuation Benefits</h3>
            <p className="text-gray-300 max-w-3xl mx-auto">
              While online estimates provide quick insights, professional valuations offer the accuracy and 
              detail needed for major financial decisions. The investment in professional services often pays 
              for itself through better pricing strategies and negotiation power.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold mb-2">Credibility</h4>
              <p className="text-gray-300 text-sm">
                Professional appraisals carry weight with lenders, buyers, and in legal proceedings.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold mb-2">Accuracy</h4>
              <p className="text-gray-300 text-sm">
                Trained professionals provide detailed analysis that online tools cannot match.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-line text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-semibold mb-2">Insights</h4>
              <p className="text-gray-300 text-sm">
                Professional reports identify value-enhancement opportunities and market positioning.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
